/**
 * Meta category attributes — the per-category fields the Meta agent generates
 * (`shoe_type`, `pant_fit`, `neckline`, `fb_product_category`, …). workflow-service
 * stores them in `meta_commerce_product_enrichment.category_attributes` and returns
 * them as `categoryAttributes` on the Meta preview and enrichment reads. One helper
 * so the listing columns, the sample modal and the product detail page label and
 * flatten them identically.
 */

import type {
  MetaCategoryAttributeValue,
  MetaCategoryAttributes,
} from '~/types/meta-category-attributes';

/** One displayable category attribute. */
export interface MetaCategoryAttributeEntry {
  /** snake_case key exactly as stored (`shoe_type`). */
  key: string;
  /** Human label (`Shoe Type`). */
  label: string;
  /** Display string; lists are joined with `, `. */
  value: string;
  /** The wire value, untouched. */
  raw: MetaCategoryAttributeValue;
}

/** Keys whose Title Cased form would read wrong (an abbreviation). */
const LABEL_OVERRIDES: Record<string, string> = {
  fb_product_category: 'Facebook Product Category',
  spf_value: 'SPF Value',
};

/** snake_case key → Title Case label (`shoe_type` → "Shoe Type"), matching the
 *  neighbouring Enriched Attributes rows and listing headers. */
export function metaCategoryAttributeLabel(key: string): string {
  const override = LABEL_OVERRIDES[key];
  if (override) return override;
  return key
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Flattens one value to display text; undefined when empty so the row is skipped. */
function toDisplay(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : undefined;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    const items = value
      .map((item) =>
        typeof item === 'boolean'
          ? item
            ? 'Yes'
            : 'No'
          : typeof item === 'string' || typeof item === 'number'
            ? String(item).trim()
            : '',
      )
      .filter(Boolean);
    return items.length > 0 ? items.join(', ') : undefined;
  }
  return undefined;
}

/** Every non-empty category attribute, in the order the payload lists them.
 *  Tolerates null/absent and non-object payloads (older rows carry none). */
export function metaCategoryAttributeEntries(
  attributes: MetaCategoryAttributes | null | undefined,
): MetaCategoryAttributeEntry[] {
  if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) return [];
  const out: MetaCategoryAttributeEntry[] = [];
  for (const [key, raw] of Object.entries(attributes)) {
    const value = toDisplay(raw);
    if (value === undefined) continue;
    out.push({ key, label: metaCategoryAttributeLabel(key), value, raw });
  }
  return out;
}
